import type { TFunction } from 'i18next'
import {
  ANNOTATION_RESOLVED_OPACITY,
  annotationSeverityLabel,
} from './annotation-labels'
import type { TripAnnotationMarker } from './types'

// Annotation marker enter/exit "pop", mirroring the vehicle-monitoring cluster
// markers: they spring in from nothing with a slight overshoot and shrink back out.
// Those clusters use a real GL spring on circle-radius; these markers are DOM, so
// the same feel comes from a CSS scale animation — a back-out cubic-bezier on the
// way in gives the overshoot, a plain ease-in on the way out reads as a quick
// retract. Durations picked to sit close to where the cluster spring settles.
export const ANNOTATION_POP_IN_MS = 260
export const ANNOTATION_POP_OUT_MS = 180
export const ANNOTATION_POP_IN = `annotation-pop-in ${ANNOTATION_POP_IN_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1) both`
export const ANNOTATION_POP_OUT = `annotation-pop-out ${ANNOTATION_POP_OUT_MS}ms ease-in both`
const ANNOTATION_MARKER_STYLE_ID = 'overwatch-annotation-marker-style'

/**
 * Class on every annotation popup. The stylesheet below drops MapLibre's own
 * padding on it, so `AnnotationPopupContent` can run its section dividers edge to
 * edge and pad each section itself.
 */
export const ANNOTATION_POPUP_CLASS = 'overwatch-annotation-popup'

/**
 * Inject the pop keyframes and the popup shell rule once per document. Both live
 * in a real stylesheet rather than inline: `@keyframes` can't be expressed in an
 * element's `style`, and MapLibre builds `.maplibregl-popup-content` itself, out
 * of reach of emotion's `css` prop.
 */
export const ensureAnnotationMarkerStyle = () => {
  if (document.getElementById(ANNOTATION_MARKER_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = ANNOTATION_MARKER_STYLE_ID
  style.textContent = `
    @keyframes annotation-pop-in { from { transform: scale(0); } to { transform: scale(1); } }
    @keyframes annotation-pop-out { from { transform: scale(1); } to { transform: scale(0); } }
    .${ANNOTATION_POPUP_CLASS} .maplibregl-popup-content {
      padding: 0;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 12, 21, 0.16);
      overflow: hidden;
    }
  `
  document.head.appendChild(style)
}

/**
 * Accessible name for one annotation marker. Baked into the element at build
 * time, so callers refresh it in place when the language changes rather than
 * rebuilding the marker.
 *
 * A resolved note says so: the fade that tells a sighted reader is invisible to a
 * screen reader.
 */
export const annotationMarkerAriaLabel = (
  t: TFunction,
  annotation: TripAnnotationMarker,
): string => {
  const vars = {
    severity: annotationSeverityLabel(t, annotation.severity),
    vehicleId: annotation.vehicleId,
  }
  return annotation.resolution
    ? t('mapAnnotations_marker_ariaLabelResolved', vars)
    : t('mapAnnotations_marker_ariaLabel', vars)
}

// The ring is sized to sit with the other pins on the route maps (the 30px ODD
// signs, the 32px waypoint circles); disc and glyph keep the design's
// proportions inside it.
/** Outer diameter of a marker: the white ring. */
export const ANNOTATION_MARKER_SIZE_PX = 32
/** Diameter of the severity-coloured disc inside the ring. */
const ANNOTATION_MARKER_DISC_PX = 24
/** The mic glyph, white on the disc. */
const ANNOTATION_MARKER_GLYPH_PX = 14
const DISC_ATTRIBUTE = 'data-annotation-marker-disc'

// pace-icons' MicrophoneIcon, inlined because this marker is vanilla DOM, not
// React. It's the same outline mic as the popup's severity circle, so the pin
// and its popup show one glyph.
const MICROPHONE_PATHS = [
  'M12 3a2 2 0 0 0-2 2v5a2 2 0 0 0 4 0V5a2 2 0 0 0-2-2m-2.828-.828A4 4 0 0 1 16 5v5a4 4 0 1 1-8 0V5a4 4 0 0 1 1.172-2.828',
  'M5 9a1 1 0 0 1 1 1 6 6 0 1 0 12 0 1 1 0 1 1 2 0 8 8 0 0 1-7 7.937V21h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-3.063A8 8 0 0 1 4 10a1 1 0 0 1 1-1',
]

// A white ring, with a soft shadow so it reads on any basemap, around a solid
// disc in the severity colour, with a white mic on the disc.
//
// Takes the annotation only to build an accessible name: the element is an empty div
// wrapping a decorative SVG, so without one a screen reader announces nothing at all.
// role="img" rather than "button" on purpose -- the marker is not keyboard-focusable and
// the popup is hover/click only, so announcing it as a control would promise operability
// that is not there. Making every marker type properly keyboard operable is a wider
// piece of work than this feature.
export const createAnnotationMarkerElement = (
  t: TFunction,
  color: string,
  annotation: TripAnnotationMarker,
) => {
  ensureAnnotationMarkerStyle()
  const el = document.createElement('div')
  el.setAttribute('role', 'img')
  el.setAttribute('aria-label', annotationMarkerAriaLabel(t, annotation))
  // Outer element only sizes and positions the marker. MapLibre owns its
  // `transform` (a translate rewritten on every camera move to keep the pin over
  // its coordinate), so the pop's `scale` has to live on an inner child instead —
  // sharing the property would make MapLibre's next reposition wipe the animation
  // (and vice versa). A transform never changes layout box, so the inner scaling
  // leaves this square — and thus the centre anchor — rock steady.
  Object.assign(el.style, {
    cursor: 'pointer',
    width: `${ANNOTATION_MARKER_SIZE_PX}px`,
    height: `${ANNOTATION_MARKER_SIZE_PX}px`,
    zIndex: '150',
  })
  // The ring. It is also the pop target and the resolved fade's target, so the
  // whole pin — ring, disc and glyph — scales and fades as one.
  const inner = document.createElement('div')
  Object.assign(inner.style, {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    borderRadius: '50%',
    boxShadow: '0 1px 4px rgba(0, 12, 21, 0.3)',
    transformOrigin: 'center',
    animation: ANNOTATION_POP_IN,
  })
  const disc = document.createElement('div')
  disc.setAttribute(DISC_ATTRIBUTE, '')
  Object.assign(disc.style, {
    width: `${ANNOTATION_MARKER_DISC_PX}px`,
    height: `${ANNOTATION_MARKER_DISC_PX}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  })
  // Static markup only: nothing from the annotation is interpolated here.
  disc.innerHTML = `<svg width="${ANNOTATION_MARKER_GLYPH_PX}" height="${ANNOTATION_MARKER_GLYPH_PX}" viewBox="0 0 24 24" fill="none" aria-hidden="true">${MICROPHONE_PATHS.map(
    d =>
      `<path fill="white" fill-rule="evenodd" clip-rule="evenodd" d="${d}"/>`,
  ).join('')}</svg>`
  inner.appendChild(disc)
  el.appendChild(inner)
  setAnnotationMarkerColor(el, color)
  setAnnotationMarkerResolved(el, annotation.resolution !== null)
  return el
}

/**
 * Recolours a marker built by `createAnnotationMarkerElement` in place.
 *
 * Exists because a marker's colour is no longer fixed for its lifetime: a caller
 * that colours by something other than severity (the triage page colours by who
 * labelled the note) sees that change on a refetch — a model prediction a person
 * then confirms — and rebuilding the marker for it would replay the pop-in on a
 * note that never left the map. The colour fills the disc; the ring and the
 * glyph stay white. It is set as a style property rather than interpolated into
 * markup, so the colour string is never parsed as HTML.
 */
/**
 * Calls `beforeOpen` just before a key opens the marker's popup.
 *
 * MapLibre's `Marker.setPopup` makes the element focusable and toggles the
 * popup on Enter or Space, from a `keypress` listener on the element. This one
 * listens in the capture phase on the same element, so it runs first: content
 * mounted here is in place when the popup opens and is measured. Mirrors
 * MapLibre's key check, so no other key mounts anything.
 *
 * Returns a function that removes the listener.
 */
export const beforeMarkerKeyboardOpen = (
  el: HTMLElement,
  beforeOpen: () => void,
): (() => void) => {
  const onKeyPress = (event: KeyboardEvent) => {
    if (event.code === 'Enter' || event.code === 'Space') beforeOpen()
  }
  el.addEventListener('keypress', onKeyPress, { capture: true })
  return () => el.removeEventListener('keypress', onKeyPress, { capture: true })
}

export const setAnnotationMarkerColor = (el: HTMLElement, color: string) => {
  const disc = el.querySelector(`[${DISC_ATTRIBUTE}]`)
  if (disc instanceof HTMLElement) disc.style.backgroundColor = color
}

/**
 * Fades a marker built by `createAnnotationMarkerElement` once its note is
 * resolved, and restores it once the resolution is withdrawn — in place, for the
 * same reason as `setAnnotationMarkerColor`.
 *
 * The fade goes on the inner wrapper, not the element MapLibre positions:
 * MapLibre writes `opacity` on that one itself (for markers behind terrain), and
 * the two would overwrite each other.
 */
export const setAnnotationMarkerResolved = (
  el: HTMLElement,
  resolved: boolean,
) => {
  const inner = el.firstElementChild
  if (!(inner instanceof HTMLElement)) return
  inner.style.opacity = resolved ? String(ANNOTATION_RESOLVED_OPACITY) : ''
}
